import React, { useEffect, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Locale } from '../../../localization/LocalizationKeys';
import { useLocalization } from '../../util/useLocalization';
import EntityTable from '../../components/entitiesSearch/results/EntityTable';
import { useTableColumns } from '../../components/entitiesSearch/results/useTableColumns';
import { buildLinkConnectionDataQuery } from '../link/LinkTable';
import { useMassEntityValidationLogAction } from '../person/Components/attributeFields/useMassEntityValidationLogAction';
import {
  ActiveEnum,
  EntityTypeEnum,
  SitePersonAffiliationsQueryQuery,
  SitePersonAffiliationsQueryQueryVariables,
  SitePersonCriteria, SitePersonCriteriaOrder
} from '../../../gql/typings';
import { useAddPersonToSiteAction } from '../site/components/AddPersonsToSiteModal/useAddPersonToSiteAction';
import { usePermission } from '../../permission/usePermission';
import { useDetailsContext } from '../../components/DetailsView/useDetailsContext';
import { useRemoveAffiliationsFromSite } from './useRemoveAffiliationsFromSite';
import { useTableRowSelection } from '../../components/Table/useTableRowSelection';


type PersonAffiliationsTableProps = {
  siteId: number;
};

const SitePersonAffiliationsTable: React.FC<PersonAffiliationsTableProps> = ({ siteId }) => {
  const localization = useLocalization();

  const tableSelection = useTableRowSelection({ virtualSupport: false });
  const [includeInactive, setIncludeInactive] = useState(false);
  const { read } = usePermission('SEARCH_INACTIVE');

  const { query, sorting } = useTableColumns(EntityTypeEnum.AFFILIATION_FROM_SITE);
  const documentNode = buildLinkConnectionDataQuery(query);
  const dataVariables: { criteria: SitePersonCriteria } = {
    criteria: {
      siteId,
      isActive: read && includeInactive ? ActiveEnum.BOTH : ActiveEnum.ACTIVE,
      ordering: sorting?.map(s => s.currentEnum as SitePersonCriteriaOrder),
      fetchSize: {
        limit: tableSelection.tableProps.perPageState[0],
        offset: (tableSelection.tableProps.pageState[0] - 1) * tableSelection.tableProps.perPageState[0],
      },
    }
  };
  const {
    data,
    error,
    loading,
    refetch,
  } = useQuery(documentNode, { variables: dataVariables });
  const {
    data: lookupData,
    refetch: refetchLookupData
  } = useQuery<SitePersonAffiliationsQueryQuery, SitePersonAffiliationsQueryQueryVariables>(DATA_QUERY, {
    variables: { siteId },
  });

  useDetailsContext(() => { refetch(); refetchLookupData(); });

  const isMaintainerApb = lookupData?.site?.maintainerSourceCode === 'APB';
  const massValidateAction = useMassEntityValidationLogAction(tableSelection, EntityTypeEnum.AFFILIATION);
  const addPersonToSiteAction = useAddPersonToSiteAction(siteId, lookupData?.site?.countryCode);
  const removePersonsAction = useRemoveAffiliationsFromSite(
    siteId,
    tableSelection,
    isMaintainerApb,
    data?.connection.totalCount,
  );

  useEffect(() => {
    if (!loading) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="person-affiliations-table-container">
      <EntityTable
        tableSelection={tableSelection}
        entityType={EntityTypeEnum.AFFILIATION_FROM_SITE}
        data={{
          nodes: data?.connection?.nodes ?? [],
          totalCount: data?.connection?.totalCount,
          error,
          loading,
        }}
        actionsToTake={[
          {
            key: 'include-inactive',
            hide: !read,
            onClick: () => setIncludeInactive(a => !a),
            label: includeInactive
              ? localization.formatMessage(Locale.Command.Only_active)
              : localization.formatMessage(Locale.Command.Include_inactive),
          },
        ]}
        quickActions={[
          massValidateAction,
          addPersonToSiteAction.action,
          removePersonsAction.action,
        ]}
      />
      {addPersonToSiteAction.hidden}
      {removePersonsAction.hidden}
    </div>
  );
};


const DATA_QUERY = gql`
  query SitePersonAffiliationsQuery($siteId: Int!) {
    site(siteId: $siteId) {
      id
      countryCode
      maintainerSourceCode
      affiliations {
        hash
        nodes {
          id
          persId
          siteId
        }
      }
    }
  }
`;


export default SitePersonAffiliationsTable;
